import { PRODUCT_TYPE } from '@ahmdigital/constants';

import type { JourneyProductType, StepDefinition, StepSlug } from './types';
import type { RootState } from '../../reducers';

// Identifiers for each step in the BYOC flow, used for routing, component mapping, and navigation
export const STEP_SLUG = {
  ABOUT_YOU: 'about-you',
  CHOOSE_COVER: 'choose',
  EXTRAS: 'extras-services',
  HOSPITAL: 'hospital-services',
} as const;

export const VALID_JOURNEY_TYPES = [PRODUCT_TYPE.EXTRAS, PRODUCT_TYPE.HOSPITAL, PRODUCT_TYPE.PACKAGE] as const;

// Metadata for each step: display labels, analytics tracking labels, and entry guards that restrict access until the customer has a refined price
export const STEP_DEFINITIONS: Record<StepSlug, StepDefinition> = {
  [STEP_SLUG.ABOUT_YOU]: {
    analyticsLabel: 'About',
    isCompleted: (state: RootState) => state.customer.hasRefinedPrice,
    label: 'About you',
    slug: STEP_SLUG.ABOUT_YOU,
  },
  [STEP_SLUG.CHOOSE_COVER]: {
    analyticsLabel: 'Choose cover',
    entryGuard: (state, journeyType) => {
      const { hasRefinedPrice } = state.customer;
      const isExtrasJourney = journeyType === PRODUCT_TYPE.EXTRAS;
      const isHospitalJourney = journeyType === PRODUCT_TYPE.HOSPITAL;
      const isPackageJourney = journeyType === PRODUCT_TYPE.PACKAGE;
      const { selectedServices, skippedSteps } = state.buildYourCover;
      const hospitalCompleted = selectedServices.hospital.length > 0 || skippedSteps.hospital;
      const extrasCompleted = selectedServices.extras.length > 0 || skippedSteps.extras;
      return (
        hasRefinedPrice &&
        ((isExtrasJourney && extrasCompleted) ||
          (isHospitalJourney && hospitalCompleted) ||
          (isPackageJourney && hospitalCompleted && extrasCompleted))
      );
    },
    label: 'Choose the cover',
    slug: STEP_SLUG.CHOOSE_COVER,
  },
  [STEP_SLUG.EXTRAS]: {
    analyticsLabel: 'Extras',
    entryGuard: (state, journeyType) => {
      const { hasRefinedPrice } = state.customer;
      const isExtrasJourney = journeyType === PRODUCT_TYPE.EXTRAS;
      const isPackageJourney = journeyType === PRODUCT_TYPE.PACKAGE;
      const hospitalCompleted =
        state.buildYourCover.selectedServices.hospital.length > 0 || state.buildYourCover.skippedSteps.hospital;
      return hasRefinedPrice && (isExtrasJourney || (isPackageJourney && hospitalCompleted));
    },
    isCompleted: (state) =>
      state.buildYourCover.selectedServices.extras.length > 0 || state.buildYourCover.skippedSteps.extras,
    label: 'Extras services',
    slug: STEP_SLUG.EXTRAS,
  },
  [STEP_SLUG.HOSPITAL]: {
    analyticsLabel: 'Hospital',
    entryGuard: (state, journeyType) => state.customer.hasRefinedPrice && journeyType !== PRODUCT_TYPE.EXTRAS,
    isCompleted: (state) =>
      state.buildYourCover.selectedServices.hospital.length > 0 || state.buildYourCover.skippedSteps.hospital,
    label: 'Hospital services',
    slug: STEP_SLUG.HOSPITAL,
  },
};

// Steps shared across all journey types (e.g. about-you). Common steps omit the journey type from their URL path
export const COMMON_STEPS: StepSlug[] = [STEP_SLUG.ABOUT_YOU];

// Checks whether a step is common (shared) vs journey-specific, used to determine URL structure
export const isCommonStep = (slug: StepSlug): boolean => COMMON_STEPS.includes(slug);

// Product-specific step sequences (excludes common steps). Defines the order of journey-specific steps per product type
export const JOURNEY_STEPS: Record<JourneyProductType, StepSlug[]> = {
  [PRODUCT_TYPE.EXTRAS]: [STEP_SLUG.EXTRAS, STEP_SLUG.CHOOSE_COVER],
  [PRODUCT_TYPE.HOSPITAL]: [STEP_SLUG.HOSPITAL, STEP_SLUG.CHOOSE_COVER],
  [PRODUCT_TYPE.PACKAGE]: [STEP_SLUG.HOSPITAL, STEP_SLUG.EXTRAS, STEP_SLUG.CHOOSE_COVER],
};

// Complete step sequences per product type (common + journey-specific). Used by navigation, step guards, and context to determine step order and adjacency
export const FLOW_SLUGS: Record<JourneyProductType, StepSlug[]> = {
  [PRODUCT_TYPE.EXTRAS]: [...COMMON_STEPS, ...JOURNEY_STEPS[PRODUCT_TYPE.EXTRAS]],
  [PRODUCT_TYPE.HOSPITAL]: [...COMMON_STEPS, ...JOURNEY_STEPS[PRODUCT_TYPE.HOSPITAL]],
  [PRODUCT_TYPE.PACKAGE]: [...COMMON_STEPS, ...JOURNEY_STEPS[PRODUCT_TYPE.PACKAGE]],
};

// Maps internal JourneyProductType to its URL path segment
export const JOURNEY_TYPE_URL_SLUG: Record<JourneyProductType, string> = {
  [PRODUCT_TYPE.EXTRAS]: 'extras',
  [PRODUCT_TYPE.HOSPITAL]: 'hospital',
  [PRODUCT_TYPE.PACKAGE]: 'bundle',
};

// Reverse map: URL path segment → JourneyProductType
export const URL_SLUG_TO_JOURNEY_TYPE: Record<string, JourneyProductType> = Object.fromEntries(
  Object.entries(JOURNEY_TYPE_URL_SLUG).map(([type, slug]) => [slug, type]),
) as Record<string, JourneyProductType>;
